/**
 * Loads the tasks board once for the whole shell — the tasks themselves, the
 * type catalogue and the contacts book — and hands the same three lists to
 * every screen. Reads go through `src/db/task-store.ts`; nothing here talks to
 * Dexie directly.
 *
 * The board also re-reads itself: it subscribes to the global reactive store,
 * so a task opened, closed or deleted anywhere in the application lands on
 * every screen holding this list without anybody calling `reload()` by hand.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  getContacts,
  getTaskTypes,
  getTasks,
  purgeExpiredTasks,
  seedTaskData,
} from '../../db/task-store';
import { useStoreEvents } from '../../hooks/use-store-events';
import type { Task, TaskType } from '../../types/task';
import type { Contact } from '../../types/contact';

export interface TasksData {
  tasks: Task[];
  taskTypes: TaskType[];
  contacts: Contact[];
  loading: boolean;
  /** Re-reads all three tables — call it after every mutation. */
  reload: () => Promise<void>;
}

export function useTasks(): TasksData {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const [nextTasks, nextTypes, nextContacts] = await Promise.all([
      getTasks(),
      getTaskTypes(),
      getContacts(),
    ]);
    setTasks(nextTasks);
    setTaskTypes(nextTypes);
    setContacts(nextContacts);
  }, []);

  useEffect(() => {
    void (async () => {
      await seedTaskData();
      // The recycle bin's 30-day retention is swept whenever the board opens,
      // so an installation left alone for a month still tidies itself.
      await purgeExpiredTasks();
      await reload();
      setLoading(false);
    })();
  }, [reload]);

  // Anything that writes a task or a convoy changes what this list should show.
  useStoreEvents(reload, ['tasks', 'assignments']);

  return { tasks, taskTypes, contacts, loading, reload };
}
